// Ahoy Analytics Tracking Helper
// This file provides easy-to-use functions for tracking events throughout the application

class AhoyAnalytics {
  constructor() {
    this.initialized = false;
    this.eventListenersAttached = false;
    this.lastTrackedPath = null;
    this.init();
  }

  init() {
    // Prevent re-initialization
    if (this.initialized) {
      return;
    }
    
    if (typeof ahoy !== 'undefined') {
      this.initialized = true;
      console.log('✅ Ahoy Analytics initialized');
      
      // Track initial page view
      this.trackPageView();
      
      // Set up automatic tracking (only once)
      if (!this.eventListenersAttached) {
        this.setupLinkTracking();
        this.setupFormTracking();
        this.setupButtonTracking();
        this.eventListenersAttached = true;
      }
    } else {
      console.warn('⚠️ Ahoy not loaded yet');
      // Retry after a short delay (max 10 retries)
      this._initRetries = (this._initRetries || 0) + 1;
      if (this._initRetries < 10) {
        setTimeout(() => this.init(), 100);
      }
    }
  }

  // Track custom events
  trackEvent(eventName, properties = {}) {
    if (this.initialized && typeof ahoy !== 'undefined') {
      ahoy.track(eventName, properties);
      console.log(`📊 Tracked event: ${eventName}`, properties);
    }
  }

  // Track page views (with deduplication)
  trackPageView() {
    if (!this.initialized) return;
    
    const currentPath = window.location.pathname + window.location.search;
    
    // Prevent duplicate tracking of the same page
    if (this.lastTrackedPath === currentPath) {
      return;
    }
    
    this.lastTrackedPath = currentPath;
    
    const pageData = {
      page: window.location.pathname,
      title: document.title,
      referrer: document.referrer
    };
    this.trackEvent('page_view', pageData);
  }

  // Set up automatic link tracking
  setupLinkTracking() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[data-track]');
      if (link) {
        const eventName = link.dataset.track || 'link_click';
        const properties = {
          text: link.textContent.trim(),
          href: link.href,
          target: link.target
        };
        
        // Add any additional data attributes
        Object.keys(link.dataset).forEach(key => {
          if (key.startsWith('track') && key !== 'track') {
            const propKey = key.replace('track', '').toLowerCase();
            properties[propKey] = link.dataset[key];
          }
        });
        
        this.trackEvent(eventName, properties);
      }
    });
  }

  // Set up form tracking
  setupFormTracking() {
    // Track form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-track]');
      if (form) {
        const eventName = form.dataset.track || 'form_submit';
        const properties = {
          form_id: form.id,
          form_action: form.action,
          form_method: form.method
        };
        
        // Add any additional data attributes
        Object.keys(form.dataset).forEach(key => {
          if (key.startsWith('track') && key !== 'track') {
            const propKey = key.replace('track', '').toLowerCase();
            properties[propKey] = form.dataset[key];
          }
        });
        
        this.trackEvent(eventName, properties);
      }
    });

    // Track form field focus (user engagement)
    document.addEventListener('focus', (e) => {
      const field = e.target.closest('input[data-track-focus], textarea[data-track-focus], select[data-track-focus]');
      if (field) {
        this.trackEvent('form_field_focus', {
          field_name: field.name,
          field_type: field.type,
          form_id: field.form?.id
        });
      }
    }, true);
  }

  // Set up button tracking
  setupButtonTracking() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest('button[data-track], [role="button"][data-track]');
      if (button && !button.closest('form')) { // Don't double-track form buttons
        const eventName = button.dataset.track || 'button_click';
        const properties = {
          text: button.textContent.trim(),
          button_id: button.id,
          button_class: button.className
        };
        
        // Add any additional data attributes
        Object.keys(button.dataset).forEach(key => {
          if (key.startsWith('track') && key !== 'track') {
            const propKey = key.replace('track', '').toLowerCase();
            properties[propKey] = button.dataset[key];
          }
        });
        
        this.trackEvent(eventName, properties);
      }
    });
  }

  // Track video plays
  trackVideoPlay(videoId, videoTitle) {
    this.trackEvent('video_play', {
      video_id: videoId,
      video_title: videoTitle
    });
  }

  // Track video completion
  trackVideoComplete(videoId, videoTitle, duration) {
    this.trackEvent('video_complete', {
      video_id: videoId,
      video_title: videoTitle,
      duration: duration
    });
  }

  // Track search queries
  trackSearch(query, resultsCount) {
    this.trackEvent('search', {
      query: query,
      results_count: resultsCount
    });
  }

  // Track file downloads
  trackDownload(fileName, fileType) {
    this.trackEvent('file_download', {
      file_name: fileName,
      file_type: fileType
    });
  }

  // Track social shares
  trackSocialShare(platform, contentType, contentId) {
    this.trackEvent('social_share', {
      platform: platform,
      content_type: contentType,
      content_id: contentId
    });
  }

  // Track wrestler profile views
  trackWrestlerView(wrestlerId, wrestlerName) {
    this.trackEvent('wrestler_view', {
      wrestler_id: wrestlerId,
      wrestler_name: wrestlerName
    });
  }

  // Track match views
  trackMatchView(matchId, eventId) {
    this.trackEvent('match_view', {
      match_id: matchId,
      event_id: eventId
    });
  }

  // Track event views
  trackEventView(eventId, eventName) {
    this.trackEvent('event_view', {
      event_id: eventId,
      event_name: eventName
    });
  }

  // Track sign up funnel steps
  trackSignUpStep(step, completionPercentage) {
    this.trackEvent('signup_step', {
      step: step,
      completion: completionPercentage
    });
  }

  // Track wrestler creation workflow
  trackWrestlerCreationStep(step, data = {}) {
    this.trackEvent('wrestler_creation_step', {
      step: step,
      ...data
    });
  }

  // Track match scheduling
  trackMatchScheduling(matchType, participants) {
    this.trackEvent('match_scheduled', {
      match_type: matchType,
      participants: participants
    });
  }

  // Track image generation
  trackImageGeneration(type, prompt) {
    this.trackEvent('image_generation', {
      type: type,
      prompt_length: prompt?.length || 0
    });
  }

  // Track errors
  trackError(errorType, errorMessage) {
    this.trackEvent('error', {
      error_type: errorType,
      error_message: errorMessage,
      page: window.location.pathname
    });
  }

  // Track conversions
  trackConversion(conversionType, value = null) {
    this.trackEvent('conversion', {
      type: conversionType,
      value: value
    });
  }

  // Track time on page
  trackTimeOnPage() {
    const startTime = Date.now();
    
    window.addEventListener('beforeunload', () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      this.trackEvent('time_on_page', {
        page: window.location.pathname,
        seconds: timeSpent
      });
    });
  }
}

// Initialize analytics (singleton pattern to prevent duplicates)
function initializeAnalytics() {
  // Prevent multiple instances
  if (window.analytics && window.analytics.initialized) {
    return;
  }
  
  window.analytics = new AhoyAnalytics();
  window.analytics.trackTimeOnPage();
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAnalytics, { once: true });
} else {
  // DOM already ready
  initializeAnalytics();
}

// Handle Turbo navigation (track new page views on navigation)
document.addEventListener('turbo:load', () => {
  if (window.analytics && window.analytics.initialized) {
    // Reset lastTrackedPath to allow tracking the new page
    window.analytics.lastTrackedPath = null;
    window.analytics.trackPageView();
  }
});

// Make analytics available globally
window.AhoyAnalytics = AhoyAnalytics;

export default AhoyAnalytics;


